import React, { FC, useRef, forwardRef, useState, useEffect, useCallback, useMemo } from "react";
import { makeStyles, Theme } from "@material-ui/core/styles";
import MaterialTable, { MTableToolbar } from "material-table";
import {
  TablePagination,
  CircularProgress,
  Box,
  Avatar,
  IconButton,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Button,
  Divider,
} from "@material-ui/core";
import { localizationJapanese } from "../../common/MatelialTableLocalizationJapanese";
import TenpoCheckManagerAnalysisSmallItemAttach from "./TenpoCheckManagerAnalysisSmallItemAttach";
import { postRequest } from "../../../apis/axiosAction";
import { getJSTDateTimeForAPI, getCurrentWeekMonday } from "../../../apis/util";
import { getZocodeInfo } from "../Register/TenpoCheckTableDataOperation";
import AddBox from "@material-ui/icons/AddBox";
import ArrowDownward from "@material-ui/icons/ArrowDownward";
import Check from "@material-ui/icons/Check";
import ChevronLeft from "@material-ui/icons/ChevronLeft";
import ChevronRight from "@material-ui/icons/ChevronRight";
import Clear from "@material-ui/icons/Clear";
import DeleteOutline from "@material-ui/icons/DeleteOutline";
import Edit from "@material-ui/icons/Edit";
import FilterList from "@material-ui/icons/FilterList";
import FirstPage from "@material-ui/icons/FirstPage";
import LastPage from "@material-ui/icons/LastPage";
import Remove from "@material-ui/icons/Remove";
import SaveAlt from "@material-ui/icons/SaveAlt";
import Search from "@material-ui/icons/Search";
import ViewColumn from "@material-ui/icons/ViewColumn";
import RadioButtonUnchecked from "@material-ui/icons/RadioButtonUnchecked";
import CloseIcon from "@material-ui/icons/Close";
import RemoveIcon from "@material-ui/icons/Remove";
import { SMALL_TITLE_VALUES, FLAG_VALUES } from "../constants";

// 評価記号の定数定義
const EVALUATION_SYMBOLS = {
  GOOD: "評価:〇",
  BAD: "評価:×", 
  NONE: "評価:ー",
} as const;

// フィルターラベルの定数定義
const FILTER_LABELS = {
  PHOTO_YES: "写真あり",
  PHOTO_NO: "写真なし",
  COMMENT_YES: "コメントあり",
  COMMENT_NO: "コメントなし",
} as const;

// フィルターセクションタイトルの定数定義
const FILTER_SECTION_TITLES = {
  EVALUATION: "―評価―",
  PHOTO: "―写真―",
  COMMENT: "―コメント―",
} as const;

// フィルター値の定数定義
const FILTER_VALUES = {
  EVALUATION: {
    GOOD: "1",
    BAD: "0",
    NONE: "-1",
  },
  PHOTO: {
    YES: "photo_yes",
    NO: "photo_no",
  },
  COMMENT: {
    YES: "comment_yes",
    NO: "comment_no",
  },
} as const;

// 評価スコアの定数定義（数値）
const EVALUATION_SCORES = {
  GOOD: 1,    // 良い評価（〇）
  BAD: 0,     // 悪い評価（×）
  NONE: -1,   // 評価なし（ー）
} as const;

// APIスコアから内部スコアへの変換マッピング
const API_SCORE_MAPPING = {
  GOOD: [2.0, 2, "2.0", "2"] as readonly (number | string)[],  // APIスコア 2.0 → 内部スコア 1 (GOOD)
  BAD: [0.0, 0, "0.0", "0"] as readonly (number | string)[],   // APIスコア 0.0 → 内部スコア 0 (BAD)
  NONE: [1.0, 1, "1.0", "1"] as readonly (number | string)[],  // APIスコア 1.0 → 内部スコア -1 (NONE)
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: "100%",
  },
  green: {
    backgroundColor: "#4caf50",
    width: 30,
    height: 30,
    border: "2px solid #000",
  },
  red: {
    backgroundColor: "#f44336",
    width: 30,
    height: 30,
    border: "2px solid #000",
  },
  gray: {
    backgroundColor: "#9e9e9e",
    width: 30,
    height: 30,
    border: "2px solid #000",
  },
  toolbar: {
    '& .MuiToolbar-root': {
      minHeight: '0px !important',
      padding: '0px !important',
    },
    '& .MuiToolbar-gutters': {
      paddingLeft: '0px !important',
      paddingRight: '0px !important',
    },
    '& .MuiToolbar-regular': {
      minHeight: '0px !important',
    },
    // ツールバー内のボタンのマージンも削除
    '& .MuiIconButton-root': {
      padding: '4px',
    }
  },
  filterButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1),
    gap: theme.spacing(1),
  },
  filterButton: {
    minWidth: 80,
  },
  tableWrapper: {
    '& .MuiPaper-root': {
      overflowX: 'auto',
    },
    '& .MuiTable-root': {
      tableLayout: 'fixed',
      minWidth: 'max-content',
      // ヘッダー行のスタイル
      '& thead tr': {
        '& th:nth-child(1)': {
          position: 'sticky !important',
          left: '0px !important',
          zIndex: '11 !important',
          backgroundColor: '#4dc0b2 !important',
          borderRight: 'none !important',
        },
        '& th:nth-child(2)': {
          position: 'sticky !important',
          left: '250px !important',
          zIndex: '11 !important',
          backgroundColor: '#4dc0b2 !important',
          borderLeft: 'none !important',
        },
      },
      // フィルター行のスタイル - より強力なセレクタで背景色を統一
      '& .MuiTableRow-root.MTableFilterRow-filterRow': {
        backgroundColor: '#f5f5f5 !important',
        '& td': {
          backgroundColor: '#f5f5f5 !important',
        },
      },
      '& tr.MTableFilterRow-filterRow, tr[class*="MTableFilterRow"]': {
        backgroundColor: '#f5f5f5 !important',
        // すべてのフィルター行セルに背景色を適用
        '& td': {
          backgroundColor: '#f5f5f5 !important',
        },
        '& td:nth-child(1)': {
          position: 'sticky !important',
          left: '0px !important',
          backgroundColor: '#f5f5f5 !important',
          zIndex: '10 !important',
          borderRight: 'none !important',
        },
        '& td:nth-child(2)': {
          position: 'sticky !important',
          left: '250px !important',
          backgroundColor: '#f5f5f5 !important',
          zIndex: '10 !important',
          borderLeft: 'none !important',
          boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)',
        },
        // 3番目以降のすべてのセルにも明示的に背景色を設定
        '& td:nth-child(n+3)': {
          backgroundColor: '#f5f5f5 !important',
        },
      },
      // データ行のスタイル
      '& tbody tr': {
        '& td:nth-child(1)': {
          position: 'sticky !important',
          left: '0px !important',
          backgroundColor: '#fff !important',
          zIndex: '10 !important',
          borderRight: 'none !important',
        },
        '& td:nth-child(2)': {
          position: 'sticky !important',
          left: '250px !important',
          backgroundColor: '#fff !important',
          zIndex: '10 !important',
          borderLeft: 'none !important',
          boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)',
        },
      },
    },
    // 最初のテンプレート項目列のみ左余白を追加
    '& .MuiTable-root th:nth-child(3)': {
      paddingLeft: '8px !important',
    },
    '& .MuiTable-root td:nth-child(3)': {
      paddingLeft: '8px !important',
    },
  },
  // データがない場合のテーブルスタイル（固定列を解除）
  tableWrapperNoData: {
    '& .MuiPaper-root': {
      overflowX: 'auto',
    },
    '& .MuiTable-root': {
      tableLayout: 'auto',
      // ヘッダー行の固定を解除
      '& thead tr th': {
        position: 'static !important',
        left: 'auto !important',
        zIndex: 'auto !important',
      },
      // フィルター行の固定を解除
      '& tr.MTableFilterRow-filterRow, tr[class*="MTableFilterRow"]': {
        '& td': {
          position: 'static !important',
          left: 'auto !important',
          zIndex: 'auto !important',
          boxShadow: 'none !important',
        },
      },
      // データ行の固定を解除
      '& tbody tr td': {
        position: 'static !important',
        left: 'auto !important',
        zIndex: 'auto !important',
        boxShadow: 'none !important',
      },
    },
  },
}));

interface TenpoCheckManagerAnalysisTemplateTableProps {
  data: any[];
  templateItems: any[];
  selectedSmallTitle: string;
  zoCode?: string;
  doCode?: string;
  selectedTemplateId?: string;
  selectedWeek?: string;
}

const TenpoCheckManagerAnalysisTemplateTable: FC<TenpoCheckManagerAnalysisTemplateTableProps> = ({
  data,
  templateItems,
  selectedSmallTitle,
  zoCode,
  doCode,
  selectedTemplateId,
  selectedWeek,
}) => {
  const classes = useStyles();
  const tableRef: any = useRef();
  const [allApiData, setAllApiData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [displayData, setDisplayData] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);


  // API呼び出し（初回のみ）
  useEffect(() => {
    const fetchData = async () => {
      let actualZoCode = zoCode;
      
      // zoCodeがなくdoCodeがある場合、doCodeからzoCodeを取得
      if (!zoCode && doCode) {
        console.log('zoCode is missing, attempting to fetch from doCode:', doCode);
        try {
          const fetchedZoCode = await getZocodeInfo(doCode.replace(/^00/, ''), 'doCode');
          if (fetchedZoCode) {
            actualZoCode = fetchedZoCode;
            console.log('Successfully fetched zoCode:', actualZoCode);
          } else {
            console.error('Failed to fetch zoCode from doCode');
            setError('データの取得に失敗しました。');
            return;
          }
        } catch (error) {
          console.error('Error fetching zoCode:', error);
          setError('データの取得に失敗しました。');
          return;
        }
      }
      
      // zoCodeとdoCodeの両方が必要
      if (!actualZoCode || !doCode) {
        console.log('Missing required codes:', { actualZoCode, doCode });
        setError('データの取得に失敗しました。');
        return;
      }
      
      console.log('Starting API call with:', { actualZoCode, doCode });
      setLoading(true);
      setError(null);
      
      try {
        const response = await postRequest(
          `${process.env.REACT_APP_API_URL_J11SPB22}/tenpocheck/analysis-small-templates`,
          {
            target_date: getJSTDateTimeForAPI(),
            zo_code: actualZoCode,
            do_code: doCode,
          }
        );
        
        console.log('API response:', response.data);
        
        if (response.data.success) {
          setAllApiData(response.data.dataList);
          console.log('API data set successfully');
        } else {
          throw new Error(response.data.message || 'API request failed');
        }
      } catch (error) {
        console.error('API call failed:', error);
        setError('データの取得に失敗しました。');
      } finally {
        setLoading(false);
        console.log('Loading set to false');
      }
    };

    fetchData();
  }, [zoCode, doCode]);

  // データ変換・フィルタリング関数
  const transformAndFilterData = (apiData: any, templateId: string, items: any[]) => {
    const rows: any[] = [];
    // selectedWeekがある場合はそれを使用、ない場合は現在の週の月曜日を取得
    const targetWeekMonday = selectedWeek || getCurrentWeekMonday();
    
    console.log('transformAndFilterData called with:', {
      templateId,
      itemsCount: items.length,
      targetWeekMonday,
      apiDataLength: apiData.length,
      templateItems: items
    });
    
    apiData.forEach((item: any) => {
      item.week_values.forEach((weekData: any) => {
        // 選択された週（または現在の週）のデータのみ処理
        if (weekData.week !== targetWeekMonday) {
          return; // 対象週でない場合はスキップ
        }
        
        console.log('Processing weekData:', {
          week: weekData.week,
          values_info_count: weekData.values_info?.length || 0,
          sample_value_info: weekData.values_info?.[0]
        });
        
        // 選択されたテンプレートに該当するデータがあるかチェック
        // テンプレートIDとテンプレートアイテムのUUIDをチェック
        const templateItemUuids = items.map(item => item.uuid || item.id);
        const hasTemplateData = weekData.values_info?.some(
          (info: any) => {
            // テンプレートアイテムのUUIDが含まれているかチェック
            return templateItemUuids.includes(info.template_item_uuid);
          }
        );
        
        console.log('Check hasTemplateData:', {
          hasTemplateData,
          templateItemUuids,
          values_info_count: weekData.values_info?.length || 0,
          sample_info: weekData.values_info?.[0]
        });
        
        // 選択されたテンプレートに該当するデータがある場合のみ行を作成
        if (weekData.values_info && weekData.values_info.length > 0 && hasTemplateData) {
          const row: any = {
            week: weekData.week,
            ofcName: weekData.base_info.ofc_name,
            name: weekData.base_info.tenpo_name,
            tenpoCode: weekData.base_info.tenpo_code,
            ofcId: weekData.base_info.ofc_id,
            ofcCode: weekData.base_info.ofc_code,
          };
          
          // 各テンプレート項目のデータをマッピング
          items.forEach((templateItem) => {
            console.log('Looking for templateItem:', templateItem);
            
            const valueInfo = weekData.values_info.find(
              (info: any) => {
                // templateItemのuuidプロパティを使用してマッチング
                const itemUuid = templateItem.uuid || templateItem.id;
                const matches = info.template_item_uuid === itemUuid;
                if (matches) {
                  console.log('Matched valueInfo for item:', itemUuid, info);
                }
                return matches;
              }
            );
            
            if (valueInfo) {
              console.log('Processing valueInfo for templateItem:', templateItem.id, valueInfo);
              console.log('Score value:', valueInfo.score, 'Type:', typeof valueInfo.score);
              
              // スコアの変換（文字列と数値の両方に対応）
              let scoreValue: number = EVALUATION_SCORES.NONE; // デフォルトは「-」
              
              // APIスコアから内部スコアへの変換
              if (API_SCORE_MAPPING.GOOD.includes(valueInfo.score) || 
                  API_SCORE_MAPPING.GOOD.includes(String(valueInfo.score))) {
                scoreValue = EVALUATION_SCORES.GOOD;
              } else if (API_SCORE_MAPPING.BAD.includes(valueInfo.score) || 
                         API_SCORE_MAPPING.BAD.includes(String(valueInfo.score))) {
                scoreValue = EVALUATION_SCORES.BAD;
              } else if (API_SCORE_MAPPING.NONE.includes(valueInfo.score) || 
                         API_SCORE_MAPPING.NONE.includes(String(valueInfo.score))) {
                scoreValue = EVALUATION_SCORES.NONE;
              }
              
              console.log('Converted score:', scoreValue);
              row[`template_${templateItem.id}`] = scoreValue;
              
              // フィルタ用フラグ
              row[`photo_${templateItem.id}`] = 
                valueInfo.photo_filepath_gcs !== "[]" ? FLAG_VALUES.EXISTS : FLAG_VALUES.NOT_EXISTS;
              row[`comment_${templateItem.id}`] = 
                (valueInfo.comment_handwrite_flag === FLAG_VALUES.EXISTS || 
                 valueInfo.user_input_comment) ? FLAG_VALUES.EXISTS : FLAG_VALUES.NOT_EXISTS;
              
              // SmallItemAttach用の詳細データ
              row[`photoFilePath_${templateItem.id}`] = valueInfo.photo_filepath_gcs;
              row[`userInputComment_${templateItem.id}`] = valueInfo.user_input_comment;
              row[`commentHandwriteFlag_${templateItem.id}`] = valueInfo.comment_handwrite_flag;
              row[`commentFilepathGcs_${templateItem.id}`] = valueInfo.comment_filepath_gcs;
            } else {
              // データが存在しない場合のデフォルト値
              row[`template_${templateItem.id}`] = EVALUATION_SCORES.NONE; // "-"表示
              row[`photo_${templateItem.id}`] = FLAG_VALUES.NOT_EXISTS;
              row[`comment_${templateItem.id}`] = FLAG_VALUES.NOT_EXISTS;
              row[`photoFilePath_${templateItem.id}`] = "";
              row[`userInputComment_${templateItem.id}`] = "";
              row[`commentHandwriteFlag_${templateItem.id}`] = FLAG_VALUES.NOT_EXISTS;
              row[`commentFilepathGcs_${templateItem.id}`] = "";
            }
          });
          
          rows.push(row);
        }
      });
    });
    
    console.log('transformAndFilterData result:', {
      rowsCount: rows.length,
      sampleRow: rows[0]
    });
    
    // データをソート: ofc_code、ofc_id、tenpo_codeの順で昇順
    rows.sort((a, b) => {
      // 1. ofc_codeで比較（昇順）
      if (a.ofcCode < b.ofcCode) return -1;
      if (a.ofcCode > b.ofcCode) return 1;
      
      // 2. ofc_idで比較（昇順）
      if (a.ofcId < b.ofcId) return -1;
      if (a.ofcId > b.ofcId) return 1;
      
      // 3. tenpo_codeで比較（昇順）
      if (a.tenpoCode < b.tenpoCode) return -1;
      if (a.tenpoCode > b.tenpoCode) return 1;
      
      return 0;
    });
    
    return rows;
  };

  // テンプレート変更時のデータフィルタリング
  useEffect(() => {
    console.log('useEffect triggered:', {
      hasAllApiData: !!allApiData,
      selectedTemplateId,
      templateItemsLength: templateItems?.length || 0,
      dataLength: data?.length || 0
    });
    
    if (allApiData && selectedTemplateId && templateItems && templateItems.length > 0) {
      const filtered = transformAndFilterData(
        allApiData, 
        selectedTemplateId, 
        templateItems
      );
      setDisplayData(filtered);
    } else {
      // API dataがある場合は既存のdataを使用
      setDisplayData(data);
    }
  }, [allApiData, selectedTemplateId, templateItems, data, selectedWeek]);

  // コンポーネントのアンマウント時のクリーンアップ（現在は不要だが、将来の拡張のために残す）
  useEffect(() => {
    return () => {
      // クリーンアップ処理（必要に応じて追加）
    };
  }, []);


  // OFCのユニークなリストを取得（displayDataから直接取得）
  const getUniqueOfcNames = useCallback(() => {
    return Array.from(new Set(displayData.map(row => row.ofcName))).sort();
  }, [displayData]);

  // カラム定義を動的に生成
  const columns = useMemo(() => {
    
    const columns: any[] = [
      {
        title: "OFC",
        field: "ofcName",
        filtering: true,  // フィルタリングを有効化
        cellStyle: {
          width: "250px",
          minWidth: "250px",
          maxWidth: "250px",
          whiteSpace: "normal",
          wordWrap: "break-word",
          wordBreak: "break-word",
          position: "sticky",
          left: 0,
          backgroundColor: "#fff",
          zIndex: 10,
          boxShadow: "2px 0 5px -2px rgba(0,0,0,0.1)",
        },
        headerStyle: {
          width: "250px",
          minWidth: "250px",
          maxWidth: "250px",
          position: "sticky",
          left: 0,
          backgroundColor: "#4dc0b2",
          zIndex: 11,
          boxShadow: "2px 0 5px -2px rgba(0,0,0,0.1)",
        },
              // カスタムフィルター設定
              customFilterAndSearch: (term: string, rowData: any) => {
                if (!term || term === '') return true; // フィルターが空の場合はすべて表示
                
                // termは複数選択された値の配列として処理
                const selectedValues = term.split(',').filter(v => v);
                if (selectedValues.length === 0) return true;
                
                // 選択されたOFCのいずれかに該当すればtrue
                return selectedValues.includes(rowData.ofcName);
              },
        filterComponent: (props: any) => {
          // FilterComponentとして独立したコンポーネントを定義
          const FilterComponent: FC = () => {
            const columnId = props.columnDef.tableData.id;
            const [isOpen, setIsOpen] = useState(false);
            
            // 現在のフィルター値を取得
            const currentFilterValues = props.columnDef.tableData.filterValue 
              ? props.columnDef.tableData.filterValue.split(',').filter((v: string) => v)
              : [];
            
            // ポップアップが開いている間の選択状態を保持するローカルステート
            const [tempSelectedValues, setTempSelectedValues] = useState<string[]>(currentFilterValues);
            
            // ポップアップが開くときに現在のフィルター値をローカルステートにセット
            const handleOpen = () => {
              setIsOpen(true);
              const currentValues = props.columnDef.tableData.filterValue 
                ? props.columnDef.tableData.filterValue.split(',').filter((v: string) => v)
                : [];
              setTempSelectedValues(currentValues);
            };
            
            // OKボタンクリック時の処理
            const handleOk = (event: React.MouseEvent) => {
              event.preventDefault();
              event.stopPropagation();
              setIsOpen(false);
              // 即座にフィルターを適用
              props.onFilterChanged(props.columnDef.tableData.columnOrder, tempSelectedValues.join(','));
            };
            
            // キャンセルボタンクリック時の処理
            const handleCancel = (event: React.MouseEvent) => {
              event.preventDefault();
              event.stopPropagation();
              // tempSelectedValuesをリセットして元の値に戻す
              const currentValues = props.columnDef.tableData.filterValue 
                ? props.columnDef.tableData.filterValue.split(',').filter((v: string) => v)
                : [];
              setTempSelectedValues(currentValues);
              setIsOpen(false);
            };
            
            // ポップアップが閉じるときの処理（外側クリックなど）
            const handleClose = (event: any) => {
              // ボタンクリックによる閉じる処理の場合は何もしない
              if (event && event.currentTarget && event.currentTarget.getAttribute('data-button-close')) {
                return;
              }
              // それ以外の場合はキャンセル扱い
              // 仮想的なMouseEventを作成してhandleCancelに渡す
              const syntheticEvent = {
                preventDefault: () => {},
                stopPropagation: () => {}
              } as React.MouseEvent;
              handleCancel(syntheticEvent);
            };
            
            const handleChange = (event: any) => {
              const values = event.target.value;
              // "clear_all"が含まれている場合は、すべての選択をクリア
              if (values.includes("clear_all")) {
                setTempSelectedValues([]);
                return;
              }
              setTempSelectedValues(values);
            };
            
            return (
              <Select
                multiple
                open={isOpen}
                value={isOpen ? tempSelectedValues : currentFilterValues}
                onChange={handleChange}
                onOpen={handleOpen}
                onClose={handleClose}
                displayEmpty
                renderValue={(selected: any) => {
                  if (selected.length === 0) {
                    return <span style={{ color: "#999" }}>選択...</span>;
                  }
                  return selected.join(', ');
                }}
                style={{ fontSize: "13px", width: "100%" }}
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                  getContentAnchorEl: null,
                  PaperProps: {
                    style: {
                      maxHeight: 400,
                      width: 250,
                      minWidth: 250,  // デフォルトのmin-widthをオーバーライド
                      paddingBottom: 0,
                    },
                    onClick: (e: any) => {
                      // メニュー内のクリックでポップアップが閉じないようにする
                      e.stopPropagation();
                    },
                  },
                  variant: 'menu',
                  disableAutoFocusItem: true,
                  disablePortal: false,
                  keepMounted: true,
                  // メニューアイテムクリックで自動的に閉じないようにする
                  autoFocus: false,
                  disableEnforceFocus: true,
                  disableRestoreFocus: true,
                }}
              >
                <MenuItem value="clear_all" style={{ backgroundColor: "#f5f5f5" }}>
                  <ListItemText primary="すべてクリア" style={{ fontWeight: "bold", color: "#1976d2" }} />
                </MenuItem>
                  {getUniqueOfcNames().map((ofcName) => (
                  <MenuItem key={ofcName} value={ofcName}>
                    <Checkbox checked={tempSelectedValues.indexOf(ofcName) > -1} />
                    <ListItemText primary={ofcName} />
                  </MenuItem>
                ))}
                <Divider />
                <Box className={classes.filterButtonContainer}>
                  <Button
                    variant="outlined"
                    size="small"
                    className={classes.filterButton}
                    onClick={handleCancel}
                    data-button-close="true"
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    キャンセル
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    className={classes.filterButton}
                    onClick={handleOk}
                    data-button-close="true"
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    OK
                  </Button>
                </Box>
              </Select>
            );
          };
          
          return <FilterComponent />;
        },
      },
      {
        title: "個店",
        field: "name",
        filtering: false,  // フィルタリングを無効化
        cellStyle: {
          width: "240px",
          minWidth: "240px",
          maxWidth: "240px",
          whiteSpace: "normal",
          wordWrap: "break-word",
          wordBreak: "break-word",
          position: "sticky",
          left: "250px",
          backgroundColor: "#fff",
          zIndex: 10,
          boxShadow: "2px 0 5px -2px rgba(0,0,0,0.1)",
        },
        headerStyle: {
          width: "240px",
          minWidth: "240px",
          maxWidth: "240px",
          position: "sticky",
          left: "250px",
          backgroundColor: "#4dc0b2",
          zIndex: 11,
          boxShadow: "2px 0 5px -2px rgba(0,0,0,0.1)",
        },
      },
    ];

    // 「全体」が選択されている場合はテンプレート項目のカラムを追加しない
    if (selectedSmallTitle !== SMALL_TITLE_VALUES.ALL) {
      // テンプレート項目のカラムを追加
      templateItems.forEach((item, index) => {
        columns.push({
          title: item.name,
          field: `template_${item.id}`,
          filtering: true,  // フィルタリングを明示的に有効化
          cellStyle: {
            minWidth: "200px",
            textAlign: "center",
          },
          headerStyle: {
            minWidth: "200px",
          },
          // カスタムフィルター設定
          customFilterAndSearch: (term: string, rowData: any) => {
            if (!term || term === '') return true; // フィルターが空の場合はすべて表示
            
            const value = rowData[`template_${item.id}`];
            const hasPhoto = rowData[`photo_${item.id}`] === FLAG_VALUES.EXISTS;
            const hasComment = rowData[`comment_${item.id}`] === FLAG_VALUES.EXISTS;
            
            // termは複数選択された値の配列として処理
            const selectedValues = term.split(',').filter(v => v);
            if (selectedValues.length === 0) return true;
            
            // 選択された条件をカテゴリごとにグループ化
            const evaluationValues: string[] = [FILTER_VALUES.EVALUATION.GOOD, FILTER_VALUES.EVALUATION.BAD, FILTER_VALUES.EVALUATION.NONE];
            const photoValues: string[] = [FILTER_VALUES.PHOTO.YES, FILTER_VALUES.PHOTO.NO];
            const commentValues: string[] = [FILTER_VALUES.COMMENT.YES, FILTER_VALUES.COMMENT.NO];
            
            const evaluationFilters = selectedValues.filter(v => evaluationValues.includes(v));
            const photoFilters = selectedValues.filter(v => photoValues.includes(v));
            const commentFilters = selectedValues.filter(v => commentValues.includes(v));
            
            // 各カテゴリのフィルター結果（カテゴリ内はOR条件）
            let passesEvaluation = evaluationFilters.length === 0 || 
              evaluationFilters.some(filter => {
                if (filter === FILTER_VALUES.EVALUATION.GOOD) return value === EVALUATION_SCORES.GOOD;
                if (filter === FILTER_VALUES.EVALUATION.BAD) return value === EVALUATION_SCORES.BAD;
                if (filter === FILTER_VALUES.EVALUATION.NONE) return value === EVALUATION_SCORES.NONE;
                return false;
              });
            
            let passesPhoto = photoFilters.length === 0 || 
              photoFilters.some(filter => {
                if (filter === FILTER_VALUES.PHOTO.YES) return hasPhoto;  // FILTER_LABELS.PHOTO_YES
                if (filter === FILTER_VALUES.PHOTO.NO) return !hasPhoto;  // FILTER_LABELS.PHOTO_NO
                return false;
              });
            
            let passesComment = commentFilters.length === 0 || 
              commentFilters.some(filter => {
                if (filter === FILTER_VALUES.COMMENT.YES) return hasComment;  // FILTER_LABELS.COMMENT_YES
                if (filter === FILTER_VALUES.COMMENT.NO) return !hasComment;  // FILTER_LABELS.COMMENT_NO
                return false;
              });
            
            // カテゴリ間はAND条件
            return passesEvaluation && passesPhoto && passesComment;
          },
          filterComponent: (props: any) => {
            // FilterComponentとして独立したコンポーネントを定義
              const FilterComponent: FC = () => {
                const columnId = props.columnDef.tableData.id;
                const [isOpen, setIsOpen] = useState(false);
                
                // 現在のフィルター値を取得
                const currentFilterValues = props.columnDef.tableData.filterValue 
                  ? props.columnDef.tableData.filterValue.split(',').filter((v: string) => v)
                  : [];
                
                // ポップアップが開いている間の選択状態を保持するローカルステート
                const [tempSelectedValues, setTempSelectedValues] = useState<string[]>(currentFilterValues);
              
                // ポップアップが開くときに現在のフィルター値をローカルステートにセット
                const handleOpen = () => {
                  setIsOpen(true);
                  const currentValues = props.columnDef.tableData.filterValue 
                    ? props.columnDef.tableData.filterValue.split(',').filter((v: string) => v)
                    : [];
                  setTempSelectedValues(currentValues);
                };
              
              // OKボタンクリック時の処理
              const handleOk = (event: React.MouseEvent) => {
                event.preventDefault();
                event.stopPropagation();
                setIsOpen(false);
                // 即座にフィルターを適用
                props.onFilterChanged(props.columnDef.tableData.columnOrder, tempSelectedValues.join(','));
              };
              
                // キャンセルボタンクリック時の処理
                const handleCancel = (event: React.MouseEvent) => {
                  event.preventDefault();
                  event.stopPropagation();
                  // tempSelectedValuesをリセットして元の値に戻す
                  const currentValues = props.columnDef.tableData.filterValue 
                    ? props.columnDef.tableData.filterValue.split(',').filter((v: string) => v)
                    : [];
                  setTempSelectedValues(currentValues);
                  setIsOpen(false);
                };
              
              // ポップアップが閉じるときの処理（外側クリックなど）
              const handleClose = (event: any) => {
                // ボタンクリックによる閉じる処理の場合は何もしない
                if (event && event.currentTarget && event.currentTarget.getAttribute('data-button-close')) {
                  return;
                }
                // それ以外の場合はキャンセル扱い
                // 仮想的なMouseEventを作成してhandleCancelに渡す
                const syntheticEvent = {
                  preventDefault: () => {},
                  stopPropagation: () => {}
                } as React.MouseEvent;
                handleCancel(syntheticEvent);
              };
              
              const handleChange = (event: any) => {
                const values = event.target.value;
                // "clear_all"が含まれている場合は、すべての選択をクリア
                if (values.includes("clear_all")) {
                  setTempSelectedValues([]);
                  return;
                }
                setTempSelectedValues(values);
              };
              
              const getItemLabel = (value: string) => {
                switch (value) {
                  case FILTER_VALUES.EVALUATION.GOOD: return EVALUATION_SYMBOLS.GOOD;
                  case FILTER_VALUES.EVALUATION.BAD: return EVALUATION_SYMBOLS.BAD;
                  case FILTER_VALUES.EVALUATION.NONE: return EVALUATION_SYMBOLS.NONE;
                  case FILTER_VALUES.PHOTO.YES: return FILTER_LABELS.PHOTO_YES;
                  case FILTER_VALUES.PHOTO.NO: return FILTER_LABELS.PHOTO_NO;
                  case FILTER_VALUES.COMMENT.YES: return FILTER_LABELS.COMMENT_YES;
                  case FILTER_VALUES.COMMENT.NO: return FILTER_LABELS.COMMENT_NO;
                  default: return "";
                }
              };
              
              return (
                <Select
                  multiple
                  open={isOpen}
                  value={isOpen ? tempSelectedValues : currentFilterValues}
                  onChange={handleChange}
                  onOpen={handleOpen}
                  onClose={handleClose}
                  displayEmpty
                  renderValue={(selected: any) => {
                    if (selected.length === 0) {
                      return <span style={{ color: "#999" }}>選択...</span>;
                    }
                    // ドロップダウンメニューの表示順に従って並び替え
                    const orderedOptions = [
                      FILTER_VALUES.EVALUATION.GOOD,
                      FILTER_VALUES.EVALUATION.BAD,
                      FILTER_VALUES.EVALUATION.NONE,
                      FILTER_VALUES.PHOTO.YES,
                      FILTER_VALUES.PHOTO.NO,
                      FILTER_VALUES.COMMENT.YES,
                      FILTER_VALUES.COMMENT.NO
                    ];
                    const orderedSelected = orderedOptions.filter(option => selected.includes(option));
                    return orderedSelected.map((v: string) => getItemLabel(v)).join(', ');
                  }}
                  style={{ fontSize: "13px", width: "100%" }}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                    getContentAnchorEl: null,
                    PaperProps: {
                      style: {
                        // maxHeight: 650,
                        minWidth: 250,  // デフォルトのmin-widthをオーバーライド
                        paddingBottom: 0,
                      },
                      onClick: (e: any) => {
                        // メニュー内のクリックでポップアップが閉じないようにする
                        e.stopPropagation();
                      },
                    },
                    variant: 'menu',
                    disableAutoFocusItem: true,
                    disablePortal: false,
                    keepMounted: true,
                    // メニューアイテムクリックで自動的に閉じないようにする
                    autoFocus: false,
                    disableEnforceFocus: true,
                    disableRestoreFocus: true,
                  }}
                >
                  <MenuItem value="clear_all" style={{ backgroundColor: "#f5f5f5" }}>
                    <ListItemText primary="すべてクリア" style={{ fontWeight: "bold", color: "#1976d2" }} />
                  </MenuItem>
                  <MenuItem disabled style={{ fontSize: "12px", fontWeight: "bold" }}>
                    <ListItemText primary={FILTER_SECTION_TITLES.EVALUATION} />
                  </MenuItem>
                  <MenuItem value={FILTER_VALUES.EVALUATION.GOOD}>
                    <Checkbox checked={tempSelectedValues.indexOf(FILTER_VALUES.EVALUATION.GOOD) > -1} />
                    <ListItemText primary={EVALUATION_SYMBOLS.GOOD} />
                  </MenuItem>
                  <MenuItem value={FILTER_VALUES.EVALUATION.BAD}>
                    <Checkbox checked={tempSelectedValues.indexOf(FILTER_VALUES.EVALUATION.BAD) > -1} />
                    <ListItemText primary={EVALUATION_SYMBOLS.BAD} />
                  </MenuItem>
                  <MenuItem value={FILTER_VALUES.EVALUATION.NONE}>
                    <Checkbox checked={tempSelectedValues.indexOf(FILTER_VALUES.EVALUATION.NONE) > -1} />
                    <ListItemText primary={EVALUATION_SYMBOLS.NONE} />
                  </MenuItem>
                  <MenuItem disabled style={{ fontSize: "12px", fontWeight: "bold" }}>
                    <ListItemText primary={FILTER_SECTION_TITLES.PHOTO} />
                  </MenuItem>
                  <MenuItem value={FILTER_VALUES.PHOTO.YES}>
                    <Checkbox checked={tempSelectedValues.indexOf(FILTER_VALUES.PHOTO.YES) > -1} />
                    <ListItemText primary={FILTER_LABELS.PHOTO_YES} />
                  </MenuItem>
                  <MenuItem value={FILTER_VALUES.PHOTO.NO}>
                    <Checkbox checked={tempSelectedValues.indexOf(FILTER_VALUES.PHOTO.NO) > -1} />
                    <ListItemText primary={FILTER_LABELS.PHOTO_NO} />
                  </MenuItem>
                  <MenuItem disabled style={{ fontSize: "12px", fontWeight: "bold" }}>
                    <ListItemText primary={FILTER_SECTION_TITLES.COMMENT} />
                  </MenuItem>
                  <MenuItem value={FILTER_VALUES.COMMENT.YES}>
                    <Checkbox checked={tempSelectedValues.indexOf(FILTER_VALUES.COMMENT.YES) > -1} />
                    <ListItemText primary={FILTER_LABELS.COMMENT_YES} />
                  </MenuItem>
                  <MenuItem value={FILTER_VALUES.COMMENT.NO}>
                    <Checkbox checked={tempSelectedValues.indexOf(FILTER_VALUES.COMMENT.NO) > -1} />
                    <ListItemText primary={FILTER_LABELS.COMMENT_NO} />
                  </MenuItem>
                  <Divider />
                  <Box className={classes.filterButtonContainer}>
                    <Button
                      variant="outlined"
                      size="small"
                      className={classes.filterButton}
                      onClick={handleCancel}
                      data-button-close="true"
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      キャンセル
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      className={classes.filterButton}
                      onClick={handleOk}
                      data-button-close="true"
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      OK
                    </Button>
                  </Box>
                </Select>
              );
            };
            
            return <FilterComponent />;
          },
          render: (rowData: any) => {
            const value = rowData[`template_${item.id}`];
            const hasPhoto = rowData[`photo_${item.id}`] === FLAG_VALUES.EXISTS;
            const hasComment = rowData[`comment_${item.id}`] === FLAG_VALUES.EXISTS;
            
            let element: React.ReactElement;
            if (value === EVALUATION_SCORES.GOOD) {
              element = (
                <Avatar className={classes.green}>
                  <IconButton>
                    <RadioButtonUnchecked style={{ fontSize: "16px" }} />
                  </IconButton>
                </Avatar>
              );
            } else if (value === EVALUATION_SCORES.BAD) {
              element = (
                <Avatar className={classes.red}>
                  <IconButton>
                    <CloseIcon style={{ fontSize: "16px" }} />
                  </IconButton>
                </Avatar>
              );
            } else {
              // value === EVALUATION_SCORES.NONE またはその他の値の場合
              element = (
                <Avatar className={classes.gray}>
                  <IconButton>
                    <RemoveIcon style={{ fontSize: "16px" }} />
                  </IconButton>
                </Avatar>
              );
            }

            return (
              <TenpoCheckManagerAnalysisSmallItemAttach
                rowData={{
                  [`photoFilePath${item.id}`]: rowData[`photoFilePath_${item.id}`],
                  [`userInputComment${item.id}`]: rowData[`userInputComment_${item.id}`],
                  [`commentHandwriteFlag${item.id}`]: rowData[`commentHandwriteFlag_${item.id}`],
                  [`commentFilepathGcs${item.id}`]: rowData[`commentFilepathGcs_${item.id}`],
                }}
                suffix={item.id.toString()}
                element={element}
              />
            );
          },
        });
      });
    }

    return columns;
  }, [templateItems, selectedSmallTitle, getUniqueOfcNames]);

  // デバッグログ
  console.log('Render state:', {
    loading,
    displayDataLength: displayData.length,
    allApiData: !!allApiData,
    error,
    templateItems: templateItems?.length || 0
  });

  // ローディング中の表示
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // エラー時の表示
  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <div style={{ color: 'red' }}>{error}</div>
      </Box>
    );
  }

  // 「全体」が選択されている場合は空の配列を返す
  const getDisplayData = (): any[] => {
    if (selectedSmallTitle === SMALL_TITLE_VALUES.ALL) {
      return [];
    }
    return displayData;
  };

  // データの有無を判定
  const tableData = getDisplayData();
  const hasData = tableData.length > 0;

  return (
    <>
      <div className={`${classes.root} ${hasData ? classes.tableWrapper : classes.tableWrapperNoData}`}>
        <MaterialTable
          tableRef={tableRef}
          data={tableData}
          columns={columns}
          components={{
            Pagination: (props) => (
              <TablePagination {...props} rowsPerPageOptions={[10, 20, 50, 100]} />
            ),
            Toolbar: (props) => (
              <div className={classes.toolbar}>
                <MTableToolbar {...props} />
              </div>
            ),
          }}
          icons={{
            Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
            Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
            Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
            Delete: forwardRef((props, ref) => (
              <DeleteOutline {...props} ref={ref} />
            )),
            DetailPanel: forwardRef((props, ref) => (
              <ChevronRight {...props} ref={ref} />
            )),
            Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
            Export: forwardRef((props, ref) => (
              <SaveAlt {...props} ref={ref} />
            )),
            Filter: forwardRef((props, ref) => (
              <FilterList {...props} ref={ref} />
            )),
            FirstPage: forwardRef((props, ref) => (
              <FirstPage {...props} ref={ref} />
            )),
            LastPage: forwardRef((props, ref) => (
              <LastPage {...props} ref={ref} />
            )),
            NextPage: forwardRef((props, ref) => (
              <ChevronRight {...props} ref={ref} />
            )),
            PreviousPage: forwardRef((props, ref) => (
              <ChevronLeft {...props} ref={ref} />
            )),
            ResetSearch: forwardRef((props, ref) => (
              <Clear {...props} ref={ref} />
            )),
            Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
            SortArrow: forwardRef((props, ref) => (
              <ArrowDownward {...props} ref={ref} />
            )),
            ThirdStateCheck: forwardRef((props, ref) => (
              <Remove {...props} ref={ref} />
            )),
            ViewColumn: forwardRef((props, ref) => (
              <ViewColumn {...props} ref={ref} />
            )),
          }}
          localization={localizationJapanese}
          options={{
            padding: "dense",
            draggable: false,
            search: false,
            showTitle: false,
            toolbar: true,  // ツールバーを有効化
            filtering: true,
            filterRowStyle: {  // フィルター行のスタイル
              backgroundColor: "#f5f5f5",
            },
            showTextRowsSelected: false,  // 選択行のテキストを非表示
            columnsButton: false,  // 列の表示/非表示ボタンを無効化
            paging: true,
            pageSize: 20,
            exportButton: false,  // エクスポートボタンを無効化（ツールバーをシンプルに）
            exportAllData: true,
            headerStyle: {
              backgroundColor: "#4dc0b2",
              color: "#ffffff",
              whiteSpace: "nowrap",
              paddingLeft: "8px",
              paddingRight: "8px",
            },
            rowStyle: {
              fontSize: "14px",
              whiteSpace: "nowrap",
              borderBottom: "1px solid #e0e0e0",
            },
          }}
          title=""
        />
      </div>
    </>
  );
};

export default TenpoCheckManagerAnalysisTemplateTable;
